import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Activity,
  Pencil,
  Trash2,
  X,
  Calendar,
  MapPinCheck,
  ShieldCheck,
  CheckSquare,
  Square,
  ListChecks,
  RotateCcw,
  Archive,
  Tent,
} from "lucide-react";
import { errorMessage, ApiError, ACTIVITY_KINDS, ACTIVITY_KIND_LABELS, ACTIVITY_KIND_NOUN } from "@atlas/shared";
import {
  fetchActivities,
  updateActivity,
  deleteActivity,
  bulkDeleteActivities,
  fetchRemovedActivities,
  restoreRemovedActivities,
} from "../api/activities";
import type { Activity as ActivityRecord, ActivityKind, RemovedActivity } from "../api/activities";
import { fetchIntegrations } from "../api/integrations";
import { AtlasPanel } from "../components/AtlasPanel";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Skel } from "../components/Skeleton";
import { PoweredByStrava, stravaActivityUrl } from "../components/StravaBrand";
import { HistoryGateLine } from "../components/HistoryGateNotice";
import { BulkRemoveDialog, kindNoun } from "../components/BulkRemoveDialog";
import { invalidateAfterActivityChange } from "../lib/activity-queries";
import { TripSuggestionCard, TripsPanel, InlineError } from "../components/TripsOnActivities";
import { useTripGrouping } from "../hooks/useTripGrouping";

const PAGE_SIZE = 25;

// Display order for the source filter. AllTrails is not a source type (its
// exports arrive as GPX), so it never appears here.
const SOURCE_ORDER = ["strava", "komoot", "apple_health", "health_connect", "garmin", "gpx"] as const;

const SOURCE_LABELS: Record<string, string> = {
  strava: "Strava",
  komoot: "Komoot",
  apple_health: "Apple Health",
  health_connect: "Health Connect",
  garmin: "Garmin",
  gpx: "GPX",
};

export interface ActivitiesSearch {
  source?: string;
  kind?: ActivityKind;
  page?: number;
  /** `?select=1` opens select mode (used by "pick them one by one" on Connected sources). */
  select?: 1;
  /** `?removed=1` shows the removed/skipped activities instead of the live list. */
  removed?: 1;
}

export function isActivityKind(v: unknown): v is ActivityKind {
  return typeof v === "string" && (ACTIVITY_KINDS as readonly string[]).includes(v);
}

// ─── Edit activity dialog ─────────────────────────────────────────────────────

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

const inputStyle: React.CSSProperties = {
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  padding: "8px 12px",
  fontSize: 14,
  color: "var(--text-primary)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

function EditActivityDialog({
  activity,
  onClose,
}: {
  activity: ActivityRecord;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState(activity.name);
  const [startDate, setStartDate] = React.useState(toDateInputValue(activity.startedAt));
  const [endDate, setEndDate] = React.useState(toDateInputValue(activity.endedAt));

  const saveMutation = useMutation({
    mutationFn: () => {
      const patch: { name?: string; startedAt?: string; endedAt?: string | null } = {};
      if (name !== activity.name) patch.name = name;
      const newStartedAt = startDate ? new Date(startDate).toISOString() : undefined;
      const origStart = toDateInputValue(activity.startedAt);
      if (startDate !== origStart) patch.startedAt = newStartedAt;
      const origEnd = toDateInputValue(activity.endedAt);
      if (endDate !== origEnd) patch.endedAt = endDate ? new Date(endDate).toISOString() : null;
      return updateActivity(activity.id, patch);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void queryClient.invalidateQueries({ queryKey: ["profile/stats"] });
      void queryClient.invalidateQueries({ queryKey: ["profile/activity-heatmap"] });
      onClose();
    },
  });

  const isMultiDay = endDate && endDate > startDate;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 420,
          width: "90%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>
            Edit activity
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4, display: "flex" }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Name</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Start date</span>
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
                End date
                <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 4 }}>(optional)</span>
              </span>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>

          {isMultiDay && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: "var(--accent-soft)",
                border: "1px solid var(--border-gold)",
                borderRadius: "var(--radius-md)",
                fontSize: 13,
                color: "var(--gold-400)",
              }}
            >
              <Calendar size={14} />
              Multi-day activity: every day counts in your heatmap
            </div>
          )}

          {saveMutation.isError && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
              {errorMessage(saveMutation.error, "Failed to save")}
            </p>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              disabled={saveMutation.isPending || !name.trim()}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Row pieces ───────────────────────────────────────────────────────────────

function ActivityDateRange({ activity }: { activity: ActivityRecord }) {
  const start = new Date(activity.startedAt);
  const startStr = start.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

  if (!activity.endedAt) return <>{startStr}</>;

  const end = new Date(activity.endedAt);
  if (end.toDateString() === start.toDateString()) return <>{startStr}</>;

  const endStr = end.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  return <>{startStr} to {endStr} · {days} days</>;
}

const iconButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--text-muted)",
  padding: 6,
  display: "flex",
  alignItems: "center",
  borderRadius: "var(--radius-sm)",
  flexShrink: 0,
};

function ActivityRow({
  activity: act,
  onEdit,
  onDelete,
  deleting,
  deletePending,
  onConfirmDelete,
  onCancelDelete,
  selectable,
  selected,
  onToggleSelect,
}: {
  activity: ActivityRecord;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
  deletePending: boolean;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const kind = isActivityKind(act.kind) ? act.kind : "other";
  return (
    <div
      onClick={selectable ? onToggleSelect : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: selected ? "var(--accent-soft)" : "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
        border: "1px solid " + (selected ? "var(--border-gold)" : "var(--border-subtle)"),
        cursor: selectable ? "pointer" : undefined,
        minWidth: 0,
      }}
    >
      {selectable ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={selected ? `Deselect ${act.name}` : `Select ${act.name}`}
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-raised)",
            border: "1px solid var(--border-default)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: selected ? "var(--text-accent)" : "var(--text-muted)",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {selected ? <CheckSquare size={16} /> : <Square size={16} />}
        </button>
      ) : (
        <span
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-raised)",
            border: "1px solid var(--border-default)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-muted)",
          }}
        >
          <Activity size={15} />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {act.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          <span>{SOURCE_LABELS[act.sourceType] ?? act.sourceType}</span>
          {kind !== "other" && (
            <span title={act.sportType ?? undefined} style={{ whiteSpace: "nowrap" }}>
              · {ACTIVITY_KIND_NOUN[kind].one.charAt(0).toUpperCase() + ACTIVITY_KIND_NOUN[kind].one.slice(1)}
            </span>
          )}
          <span style={{ whiteSpace: "nowrap" }}>· <ActivityDateRange activity={act} /></span>
          {act.distanceM != null && (
            <span style={{ whiteSpace: "nowrap" }}>· {(act.distanceM / 1000).toFixed(1)} km</span>
          )}
          {act.trip && (
            <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 3, minWidth: 0 }}>
              {"· "}
              {selectable ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <Tent size={11} /> {act.trip.name}
                </span>
              ) : (
                <Link
                  to="/trips/$tripId"
                  params={{ tripId: act.trip.id }}
                  onClick={(e) => e.stopPropagation()}
                  title={`Part of ${act.trip.name}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "var(--text-secondary)", textDecoration: "none" }}
                >
                  <Tent size={11} /> {act.trip.name}
                </Link>
              )}
            </span>
          )}
          {act.timeTrusted && (
            <span
              title="Verified times: counts towards time-bound achievements"
              style={{ display: "inline-flex", alignItems: "center", color: "var(--spruce-400)" }}
            >
              <ShieldCheck size={11} />
            </span>
          )}
          {act.sourceType === "strava" && act.sourceId && (
            <span style={{ whiteSpace: "nowrap" }}>
              {"· "}
              <a
                href={stravaActivityUrl(act.sourceId)}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--text-secondary)", textDecoration: "none" }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
              >
                View on Strava
              </a>
            </span>
          )}
        </div>
      </div>
      {!selectable && (<>
      <Link
        to="/activities/$id/review"
        params={{ id: act.id }}
        title="Review matches"
        style={{ ...iconButtonStyle, textDecoration: "none" }}
        onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.background = "var(--surface-raised)"; }}
        onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.background = "none"; }}
      >
        <MapPinCheck size={13} />
      </Link>
      <button
        onClick={onEdit}
        title="Edit activity"
        style={iconButtonStyle}
        onMouseEnter={(e) => { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.background = "var(--surface-raised)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.background = "none"; }}
      >
        <Pencil size={13} />
      </button>
      {deleting ? (
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          <button
            onClick={onConfirmDelete}
            disabled={deletePending}
            title="Confirm delete"
            style={{
              background: "var(--status-danger)",
              border: "none",
              cursor: "pointer",
              color: "var(--white)",
              padding: "4px 8px",
              display: "flex",
              alignItems: "center",
              borderRadius: "var(--radius-sm)",
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {deletePending ? "…" : "Delete"}
          </button>
          <button onClick={onCancelDelete} title="Cancel" style={{ ...iconButtonStyle, padding: 4 }}>
            <X size={13} />
          </button>
        </div>
      ) : (
        <button
          onClick={onDelete}
          title="Delete activity"
          style={iconButtonStyle}
          onMouseEnter={(e) => { e.currentTarget.style.color = "var(--status-danger)"; e.currentTarget.style.background = "var(--surface-raised)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.background = "none"; }}
        >
          <Trash2 size={13} />
        </button>
      )}
      </>)}
    </div>
  );
}

// ─── Removed row (spec 26 archive) ───────────────────────────────────────────

function formatDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const REMOVED_REASON_BADGE: Record<RemovedActivity["reason"], string> = {
  removed: "Removed",
  skipped_by_preference: "Skipped",
  duplicate: "Duplicate",
  trip_route: "Trip route",
};

const reasonLinkStyle: React.CSSProperties = { color: "var(--text-accent)", textDecoration: "none" };

/** Spec 37 / 38: why Atlas parked it, with a link to the kept copy or the trip. */
function RemovedReasonNote({ item }: { item: RemovedActivity }) {
  if (item.reason === "duplicate") {
    const kept = item.duplicateOf;
    return (
      <span style={{ minWidth: 0 }}>
        · Duplicate of{" "}
        {kept ? (
          <Link to="/activities/$id/review" params={{ id: kept.id }} onClick={(e) => e.stopPropagation()} style={reasonLinkStyle}>
            {kept.name}
          </Link>
        ) : (
          "another copy"
        )}
      </span>
    );
  }
  if (item.reason === "trip_route") {
    const trip = item.tripRouteOf;
    return (
      <span style={{ minWidth: 0 }}>
        · Used as the route for{" "}
        {trip ? (
          <Link to="/trips/$tripId" params={{ tripId: trip.id }} onClick={(e) => e.stopPropagation()} style={reasonLinkStyle}>
            {trip.name}
          </Link>
        ) : (
          "a trip"
        )}
      </span>
    );
  }
  return null;
}

function RemovedRow({
  item,
  onRestore,
  restorePending,
  selectable,
  selected,
  onToggleSelect,
}: {
  item: RemovedActivity;
  onRestore: () => void;
  restorePending: boolean;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
}) {
  const kind = isActivityKind(item.kind) ? item.kind : "other";
  return (
    <div
      onClick={selectable ? onToggleSelect : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 10px",
        background: selected ? "var(--accent-soft)" : "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
        border: "1px solid " + (selected ? "var(--border-gold)" : "var(--border-subtle)"),
        cursor: selectable ? "pointer" : undefined,
        minWidth: 0,
      }}
    >
      {selectable ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={selected ? `Deselect ${item.name}` : `Select ${item.name}`}
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-raised)",
            border: "1px solid var(--border-default)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: selected ? "var(--text-accent)" : "var(--text-muted)",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {selected ? <CheckSquare size={16} /> : <Square size={16} />}
        </button>
      ) : (
        <span
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-raised)",
            border: "1px solid var(--border-default)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--text-muted)",
          }}
        >
          <Archive size={15} />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          <span>{SOURCE_LABELS[item.sourceType] ?? item.sourceType}</span>
          {kind !== "other" && (
            <span title={item.sportType ?? undefined} style={{ whiteSpace: "nowrap" }}>
              · {ACTIVITY_KIND_NOUN[kind].one.charAt(0).toUpperCase() + ACTIVITY_KIND_NOUN[kind].one.slice(1)}
            </span>
          )}
          <span style={{ whiteSpace: "nowrap" }}>· {formatDay(item.startedAt)}</span>
          {item.distanceM != null && (
            <span style={{ whiteSpace: "nowrap" }}>· {(item.distanceM / 1000).toFixed(1)} km</span>
          )}
          <RemovedReasonNote item={item} />
        </div>
      </div>
      <Badge variant={item.reason === "removed" ? "neutral" : "info"}>{REMOVED_REASON_BADGE[item.reason] ?? "Removed"}</Badge>
      {!selectable && (
        <Button variant="ghost" size="sm" disabled={restorePending} onClick={onRestore}>
          {restorePending ? "…" : "Restore"}
        </Button>
      )}
    </div>
  );
}

// ─── Source filter ────────────────────────────────────────────────────────────

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        borderRadius: "var(--radius-pill)",
        border: "1px solid " + (active ? "var(--border-gold)" : "var(--border-subtle)"),
        background: active ? "var(--accent-soft)" : "var(--surface-card)",
        color: active ? "var(--text-accent)" : "var(--text-secondary)",
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
        transition: "background 0.15s ease, color 0.15s ease",
      }}
    >
      {label}
      {count != null && (
        <span className="tnum" style={{ fontSize: 11, color: active ? "var(--text-accent)" : "var(--text-muted)" }}>
          {count.toLocaleString("en-GB")}
        </span>
      )}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ActivitiesPage() {
  const { source, kind, page: rawPage, select, removed } = useSearch({ strict: false }) as ActivitiesSearch;
  const removedView = removed === 1;
  const page = Math.max(1, rawPage ?? 1);
  const offset = (page - 1) * PAGE_SIZE;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = React.useState<ActivityRecord | null>(null);
  const [deleting, setDeleting] = React.useState<string | null>(null);

  // Select mode (spec 26): a set of ids, or "matching" = everything the
  // current source/kind filter returns (deleted server-side by filter with
  // an expected count so a sync landing mid-dialog cannot over-delete).
  const [selectMode, setSelectMode] = React.useState<boolean>(select === 1);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());
  const [selectAllMatching, setSelectAllMatching] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [bulkError, setBulkError] = React.useState<string | null>(null);
  const [bulkNotice, setBulkNotice] = React.useState<string | null>(null);
  // ATL-175: ids of the last id-based remove, for the notice's Undo. A
  // filter-based remove has no id list, so it offers no Undo.
  const [lastRemovedIds, setLastRemovedIds] = React.useState<string[] | null>(null);

  React.useEffect(() => {
    if (select === 1) setSelectMode(true);
  }, [select]);

  // Spec 35: "Make a trip" from the selection, then open the new trip.
  const tripGrouping = useTripGrouping({
    onDone: (tripId) => {
      exitSelectMode();
      void navigate({ to: "/trips/$tripId", params: { tripId } });
    },
  });

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
    setSelectAllMatching(false);
    setConfirmOpen(false);
    setBulkError(null);
    if (select === 1) setSearch({ source, kind, page, removed });
  }

  // Switching between the live list and the removed view clears selection.
  React.useEffect(() => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
    setConfirmOpen(false);
  }, [removedView]);

  const activitiesQuery = useQuery({
    queryKey: ["activities", { source: source ?? null, kind: kind ?? null, page }],
    queryFn: () => fetchActivities(PAGE_SIZE, offset, source, kind),
    placeholderData: (prev) => prev,
    enabled: !removedView,
  });
  // Removed view (spec 26 archive): tombstones with the same source/kind
  // filters. The unfiltered `groups` summary feeds the chip counts here and
  // the "Removed · N" pill on the live view.
  const removedQuery = useQuery({
    queryKey: ["activities/removed", { source: source ?? null, kind: kind ?? null, page }],
    queryFn: () => fetchRemovedActivities({ source, kind, limit: PAGE_SIZE, offset }),
    placeholderData: (prev) => prev,
    enabled: removedView,
  });
  const removedSummaryQuery = useQuery({
    queryKey: ["activities/removed", { summary: true }],
    queryFn: () => fetchRemovedActivities({ limit: 1, offset: 0 }),
    staleTime: 30_000,
  });
  const removedGroups = (removedView ? removedQuery.data?.groups : undefined) ?? removedSummaryQuery.data?.groups ?? [];
  const removedTotalAll = removedGroups.reduce((a, g) => a + g.count, 0);
  const integrationsQuery = useQuery({
    queryKey: ["integrations"],
    queryFn: fetchIntegrations,
    staleTime: 60_000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteActivity(id),
    onSuccess: () => {
      invalidateAfterActivityChange(queryClient);
      setDeleting(null);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: () =>
      selectAllMatching
        ? bulkDeleteActivities({
            filter: { ...(kind ? { kind } : {}), ...(source ? { source } : {}) },
            expectedCount: total,
          })
        : bulkDeleteActivities({ ids: [...selectedIds] }),
    onSuccess: (res) => {
      invalidateAfterActivityChange(queryClient);
      const n = res.deleted;
      setBulkNotice(`Removed ${n.toLocaleString("en-GB")} ${kindNoun(selectAllMatching ? kind : undefined, n)}.`);
      setLastRemovedIds(selectAllMatching ? null : [...selectedIds]);
      setRestoreNotice(null);
      exitSelectMode();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ["activities"] });
        setSelectAllMatching(false);
        setBulkError("The list changed while you were choosing, so nothing was removed. Check it and try again.");
        return;
      }
      setBulkError(errorMessage(err, "Couldn't remove those. Try again."));
    },
  });

  // Restore (removed view): per row by id, or the selection (ids / everything
  // matching the filter with the count the user saw).
  const [restoringId, setRestoringId] = React.useState<string | null>(null);
  // Restoring a planned-route GPX clears its trip's route (spec 38): confirm first.
  const [confirmRouteRestore, setConfirmRouteRestore] = React.useState<RemovedActivity | null>(null);
  const [restoreNotice, setRestoreNotice] = React.useState<string | null>(null);
  const restoreMutation = useMutation({
    mutationFn: (body: Parameters<typeof restoreRemovedActivities>[0]) => restoreRemovedActivities(body),
    onSuccess: (res) => {
      invalidateAfterActivityChange(queryClient);
      const n = res.restored;
      const parts = [`Restored ${n.toLocaleString("en-GB")} ${kindNoun(selectAllMatching ? kind : undefined, n)}.`];
      if (res.restoredLocally > 0) parts.push("Re-matching now.");
      if (res.queued > 0) parts.push("Fetching the rest from the source.");
      if (res.needsDeviceSync > 0) parts.push("Open the Atlas app and sync to bring back Apple Health or Health Connect activities.");
      setRestoreNotice(parts.join(" "));
      setBulkNotice(null);
      exitSelectMode();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ["activities/removed"] });
        setSelectAllMatching(false);
        setBulkError("The list changed while you were choosing, so nothing was restored. Check it and try again.");
        return;
      }
      setBulkError(errorMessage(err, "Couldn't restore those. Try again."));
    },
    onSettled: () => setRestoringId(null),
  });

  function restoreSelection() {
    setBulkError(null);
    if (selectAllMatching) {
      restoreMutation.mutate({
        filter: { ...(kind ? { kind } : {}), ...(source ? { source } : {}) },
        expectedCount: total,
      });
    } else {
      restoreMutation.mutate({ ids: [...selectedIds] });
    }
  }

  function setSearch(next: ActivitiesSearch) {
    void navigate({
      to: "/activities",
      search: {
        source: next.source || undefined,
        kind: next.kind || undefined,
        page: next.page && next.page > 1 ? next.page : undefined,
        select: next.select,
        removed: next.removed,
      },
    });
  }

  const data = activitiesQuery.data;
  const liveActivities = data?.activities ?? [];
  const removedItems = removedQuery.data?.items ?? [];
  const activities = removedView ? [] : liveActivities;
  const listLength = removedView ? removedItems.length : liveActivities.length;
  const total = removedView ? (removedQuery.data?.total ?? removedItems.length) : (data?.total ?? liveActivities.length);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const listLoading = removedView ? removedQuery.isLoading : activitiesQuery.isLoading;
  const listError = removedView ? removedQuery.error : activitiesQuery.error;
  const listFetching = removedView ? removedQuery.isFetching : activitiesQuery.isFetching;

  // Per-source and per-kind counts are unfiltered, so the chip sets do not
  // change as the user flips between filters. In the removed view they come
  // from the tombstone groups instead.
  const counts = new Map<string, number>();
  const kindCounts = new Map<ActivityKind, number>();
  if (removedView) {
    for (const g of removedGroups) {
      counts.set(g.sourceType, (counts.get(g.sourceType) ?? 0) + g.count);
      if (isActivityKind(g.kind)) kindCounts.set(g.kind, (kindCounts.get(g.kind) ?? 0) + g.count);
    }
  } else {
    for (const s of data?.sources ?? []) counts.set(s.sourceType, s.count);
    for (const k of data?.kinds ?? []) if (isActivityKind(k.kind)) kindCounts.set(k.kind, k.count);
  }
  const allCount = [...counts.values()].reduce((a, b) => a + b, 0);
  const visibleKinds = ACTIVITY_KINDS.filter((k) => (kindCounts.get(k) ?? 0) > 0 || k === kind);
  const showKindChips = visibleKinds.length > 0 && (kindCounts.size > 1 || Boolean(kind));

  const selectedCount = selectAllMatching ? total : selectedIds.size;
  const hasFilter = Boolean(source || kind);
  const toggleSelected = (id: string) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // A source earns a chip if it has records, or if it is a connected
  // integration (so a freshly connected but still-syncing source shows up).
  // GPX import is always available. The active source is always shown so a
  // deep link never points at an invisible filter.
  const connected = new Set(
    (integrationsQuery.data?.integrations ?? [])
      .filter((row) => row.connected && !row.comingSoon && !row.manualOnly)
      .map((row) => row.provider as string),
  );
  const visibleSources = SOURCE_ORDER.filter(
    (s) => removedView
      ? (counts.get(s) ?? 0) > 0 || s === source
      : s === "gpx" || (counts.get(s) ?? 0) > 0 || connected.has(s) || s === source,
  );
  const showFilters = allCount > 0 || Boolean(source);

  // If a filter or page change lands past the last page (e.g. after deletes),
  // snap back to the last page that exists.
  React.useEffect(() => {
    const loaded = removedView ? removedQuery.data : data;
    if (loaded && total > 0 && page > totalPages) setSearch({ source, kind, page: totalPages, removed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, removedQuery.data, total, page, totalPages]);

  const sourceLabel = source ? (SOURCE_LABELS[source] ?? source) : null;
  const kindLabel = kind ? ACTIVITY_KIND_LABELS[kind] : null;
  const baseTitle = sourceLabel && kindLabel
    ? `${sourceLabel} ${kindLabel.toLowerCase()}`
    : sourceLabel
      ? `${sourceLabel} activities`
      : kindLabel
        ? kindLabel === "Other" ? "Other activities" : kindLabel
        : "All activities";
  const panelTitle = removedView ? `Removed: ${baseTitle.charAt(0).toLowerCase() + baseTitle.slice(1)}` : baseTitle;

  return (
    <div
      style={{
        overflow: "auto",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div className="atlas-topo r-page" style={{ position: "relative", padding: "28px 28px 24px" }}>
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(0deg, var(--bg-app) 0%, color-mix(in srgb, var(--bg-app) 40%, transparent) 100%)",
          }}
        />
        <div style={{ position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Your data</p>
            <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>
              Activities
            </h1>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <Link
            to="/add-from-photos"
            style={{ fontSize: 13, color: "var(--text-secondary)", textDecoration: "none" }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.textDecoration = "none"; }}
          >
            Add from photos
          </Link>
          <Link
            to="/connected-sources"
            style={{ fontSize: 13, color: "var(--text-secondary)", textDecoration: "none" }}
            onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.textDecoration = "none"; }}
          >
            Manage connected sources
          </Link>
          </div>
        </div>
      </div>

      <div className="r-page-x" style={{ padding: "0 28px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
        {showFilters && (
          <div className="r-wrap" role="group" aria-label="Filter by source" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <FilterChip
              label="All"
              count={allCount}
              active={!source}
              onClick={() => setSearch({ kind, removed })}
            />
            {visibleSources.map((s) => (
              <FilterChip
                key={s}
                label={SOURCE_LABELS[s] ?? s}
                count={counts.get(s) ?? 0}
                active={source === s}
                onClick={() => setSearch({ source: s, kind, removed })}
              />
            ))}
          </div>
        )}

        {showKindChips && (
          <div className="r-wrap" role="group" aria-label="Filter by kind" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <FilterChip
              label="Every kind"
              active={!kind}
              onClick={() => setSearch({ source, removed })}
            />
            {visibleKinds.map((k) => (
              <FilterChip
                key={k}
                label={ACTIVITY_KIND_LABELS[k]}
                count={kindCounts.get(k) ?? 0}
                active={kind === k}
                onClick={() => setSearch({ source, kind: k, removed })}
              />
            ))}
          </div>
        )}

        {bulkNotice && (
          <div
            role="status"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              fontSize: 13,
              color: "var(--text-secondary)",
              background: "var(--surface-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "8px 12px",
              minWidth: 0,
            }}
          >
            <span>{bulkNotice} You can bring them back from <Link to="/activities" search={{ removed: 1 }} style={{ color: "var(--text-accent)", textDecoration: "none" }}>Removed activities</Link>.</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {lastRemovedIds && lastRemovedIds.length > 0 && (
                <button
                  onClick={() => {
                    restoreMutation.mutate({ activityIds: lastRemovedIds });
                    setLastRemovedIds(null);
                    setBulkNotice(null);
                  }}
                  disabled={restoreMutation.isPending}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", fontWeight: 600, color: "var(--text-accent)" }}
                >
                  Undo
                </button>
              )}
              <button onClick={() => setBulkNotice(null)} aria-label="Dismiss" style={{ ...iconButtonStyle, padding: 2 }}><X size={13} /></button>
            </span>
          </div>
        )}

        {restoreNotice && (
          <div
            role="status"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              fontSize: 13,
              color: "var(--text-secondary)",
              background: "var(--surface-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              padding: "8px 12px",
              minWidth: 0,
            }}
          >
            <span>{restoreNotice}</span>
            <button onClick={() => setRestoreNotice(null)} aria-label="Dismiss" style={{ ...iconButtonStyle, padding: 2 }}><X size={13} /></button>
          </div>
        )}

        {!removedView && <TripSuggestionCard />}
        {!removedView && <TripsPanel />}

        <AtlasPanel
          title={panelTitle}
          action={
            listLoading ? null : (
              <span className="r-wrap" style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span className="eyebrow tnum" style={{ fontSize: 10 }}>
                  {total === 1 ? "1 activity" : `${total.toLocaleString("en-GB")} activities`}
                </span>
                {removedView ? (
                  <Link
                    to="/activities"
                    search={{ source, kind }}
                    style={{ fontSize: 12.5, color: "var(--text-accent)", textDecoration: "none" }}
                  >
                    Back to activities
                  </Link>
                ) : removedTotalAll > 0 && (
                  <Link
                    to="/activities"
                    search={{ source, kind, removed: 1 }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "3px 10px",
                      borderRadius: "var(--radius-pill)",
                      border: "1px solid var(--border-subtle)",
                      background: "var(--surface-sunken)",
                      color: "var(--text-secondary)",
                      fontSize: 12,
                      fontWeight: 500,
                      textDecoration: "none",
                    }}
                  >
                    <Archive size={12} />
                    Removed
                    <span className="tnum" style={{ color: "var(--text-muted)" }}>{removedTotalAll.toLocaleString("en-GB")}</span>
                  </Link>
                )}
                {listLength > 0 && (
                  selectMode ? (
                    <Button variant="ghost" size="sm" onClick={exitSelectMode}>Cancel</Button>
                  ) : (
                    <Button variant="ghost" size="sm" leftIcon={<ListChecks size={13} />} onClick={() => setSelectMode(true)}>
                      Select
                    </Button>
                  )
                )}
              </span>
            )
          }
        >
          {listLoading ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[0, 1, 2, 3, 4].map((i) => <Skel key={i} height="52px" />)}
            </div>
          ) : listError ? (
            <EmptyState
              icon={<Activity size={18} />}
              title={removedView ? "Couldn't load removed activities" : "Couldn't load activities"}
              hint={errorMessage(listError, "Try again in a moment.")}
            />
          ) : removedView ? (
            removedItems.length === 0 ? (
              <EmptyState
                icon={<Archive size={18} />}
                title={source || kind ? "Nothing removed here" : "Nothing removed"}
                hint="Activities you remove, and any Atlas skips because of your import settings, are listed here so you can bring them back."
              />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, opacity: listFetching ? 0.7 : 1, transition: "opacity 0.15s ease" }}>
                {removedItems.map((item) => (
                  <RemovedRow
                    key={item.id}
                    item={item}
                    onRestore={() => {
                      if (item.reason === "trip_route") {
                        setConfirmRouteRestore(item);
                        return;
                      }
                      setRestoringId(item.id);
                      setBulkError(null);
                      restoreMutation.mutate({ ids: [item.id] });
                    }}
                    restorePending={restoreMutation.isPending && restoringId === item.id}
                    selectable={selectMode}
                    selected={selectAllMatching || selectedIds.has(item.id)}
                    onToggleSelect={() => toggleSelected(item.id)}
                  />
                ))}
                {bulkError && !selectMode && (
                  <p style={{ fontSize: 12, color: "var(--status-danger)", margin: "6px 0 0" }}>{bulkError}</p>
                )}
                {totalPages > 1 && (
                  <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
                    <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setSearch({ source, kind, page: page - 1, removed })}>
                      Previous
                    </Button>
                    <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {(offset + 1).toLocaleString("en-GB")} to {Math.min(offset + PAGE_SIZE, total).toLocaleString("en-GB")} of {total.toLocaleString("en-GB")}
                    </span>
                    <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setSearch({ source, kind, page: page + 1, removed })}>
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )
          ) : activities.length === 0 ? (
            source ? (
              <EmptyState
                icon={<Activity size={18} />}
                title={`No ${sourceLabel} activities yet`}
                hint="Once activities arrive from this source they will be listed here."
              />
            ) : (
              <EmptyState
                icon={<Activity size={18} />}
                title="No activities yet"
                hint="Import a GPX file or connect a source on the Connected sources page."
              />
            )
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, opacity: activitiesQuery.isFetching ? 0.7 : 1, transition: "opacity 0.15s ease" }}>
              {activities.map((act) => (
                <ActivityRow
                  key={act.id}
                  activity={act}
                  onEdit={() => setEditing(act)}
                  onDelete={() => setDeleting(act.id)}
                  deleting={deleting === act.id}
                  deletePending={deleteMutation.isPending && deleting === act.id}
                  onConfirmDelete={() => deleteMutation.mutate(act.id)}
                  onCancelDelete={() => setDeleting(null)}
                  selectable={selectMode}
                  selected={selectAllMatching || selectedIds.has(act.id)}
                  onToggleSelect={() => toggleSelected(act.id)}
                />
              ))}

              {(totalPages > 1 || activities.some((a) => a.sourceType === "strava")) && (
                <div
                  className="r-wrap"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    marginTop: 10,
                  }}
                >
                  {totalPages > 1 ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setSearch({ source, kind, page: page - 1 })}
                      >
                        Previous
                      </Button>
                      <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {(offset + 1).toLocaleString("en-GB")} to {Math.min(offset + PAGE_SIZE, total).toLocaleString("en-GB")} of {total.toLocaleString("en-GB")}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setSearch({ source, kind, page: page + 1 })}
                      >
                        Next
                      </Button>
                    </div>
                  ) : <span />}
                  {activities.some((a) => a.sourceType === "strava") && (
                    <PoweredByStrava height={32} style={{ opacity: 0.85, marginLeft: "auto" }} />
                  )}
                </div>
              )}

              {/* Free plan: the foot of the list is where the oldest activity
                  sits, so it is exactly where someone scrolls looking for a
                  walk that is missing. Only on the last page. */}
              {page >= totalPages && <HistoryGateLine context="activities" style={{ marginTop: 12 }} />}
            </div>
          )}
        </AtlasPanel>
      </div>

      {/* Select-mode action bar: sticky at the foot of the scroll container. */}
      {selectMode && (
        <div
          role="toolbar"
          aria-label="Selected activities"
          className="r-wrap"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 20,
            margin: "0 28px 16px",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
            background: "var(--surface-card)",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-xl)",
            minWidth: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", minWidth: 0 }}>
            <span className="tnum" style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
              {selectedCount.toLocaleString("en-GB")} selected
            </span>
            {hasFilter && total > listLength && !selectAllMatching && (
              <button
                type="button"
                onClick={() => { setSelectedIds(new Set()); setSelectAllMatching(true); }}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text-accent)", padding: 0 }}
              >
                Select all {total.toLocaleString("en-GB")} matching
              </button>
            )}
            {!selectAllMatching && listLength > 0 && selectedIds.size < listLength && (
              <button
                type="button"
                onClick={() => setSelectedIds(new Set((removedView ? removedItems : activities).map((a) => a.id)))}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text-secondary)", padding: 0 }}
              >
                Select this page
              </button>
            )}
            {selectAllMatching && (
              <button
                type="button"
                onClick={() => setSelectAllMatching(false)}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "var(--text-secondary)", padding: 0 }}
              >
                Clear
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {bulkError && removedView && (
              <span style={{ fontSize: 12, color: "var(--status-danger)" }}>{bulkError}</span>
            )}
            {tripGrouping.error && !removedView && (
              <InlineError message={tripGrouping.error} onDismiss={tripGrouping.clearError} />
            )}
            <Button variant="ghost" size="sm" onClick={exitSelectMode}>Cancel</Button>
            {removedView ? (
              <Button
                variant="primary"
                size="sm"
                leftIcon={<RotateCcw size={13} />}
                disabled={selectedCount === 0 || restoreMutation.isPending}
                onClick={restoreSelection}
              >
                {restoreMutation.isPending
                  ? "Restoring…"
                  : selectAllMatching
                    ? `Restore all ${selectedCount.toLocaleString("en-GB")} matching`
                    : "Restore selected"}
              </Button>
            ) : (
              <>
              {!selectAllMatching && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Tent size={13} />}
                  disabled={selectedIds.size < 2 || tripGrouping.pending}
                  title={selectedIds.size < 2 ? "Select two or more activities to make a trip" : undefined}
                  onClick={() => tripGrouping.createTrip([...selectedIds])}
                >
                  {tripGrouping.pending ? "Grouping…" : "Make a trip"}
                </Button>
              )}
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Trash2 size={13} />}
                disabled={selectedCount === 0}
                onClick={() => { setBulkError(null); setConfirmOpen(true); }}
              >
                Remove
              </Button>
              </>
            )}
          </div>
        </div>
      )}

      {tripGrouping.confirmElement}

      {confirmOpen && (
        <BulkRemoveDialog
          count={selectedCount}
          kind={selectAllMatching ? kind : undefined}
          pending={bulkDeleteMutation.isPending}
          error={bulkError}
          onConfirm={() => bulkDeleteMutation.mutate()}
          onClose={() => { setConfirmOpen(false); setBulkError(null); }}
        />
      )}

      {confirmRouteRestore && (
        <ConfirmDialog
          title="Restore this activity?"
          body={
            confirmRouteRestore.tripRouteOf
              ? `It is the planned route for ${confirmRouteRestore.tripRouteOf.name}. Restoring it clears that route, so the stretches it filled leave the trip. Trail progress already earned stays.`
              : "It is a trip's planned route. Restoring it clears that route, so the stretches it filled leave the trip. Trail progress already earned stays."
          }
          confirmLabel="Restore"
          pendingLabel="Restoring…"
          pending={restoreMutation.isPending}
          error={restoreMutation.isError ? errorMessage(restoreMutation.error, "Couldn't restore it. Try again.") : null}
          onConfirm={() => {
            const item = confirmRouteRestore;
            setRestoringId(item.id);
            restoreMutation.mutate(
              { ids: [item.id] },
              { onSuccess: () => setConfirmRouteRestore(null) },
            );
          }}
          onClose={() => setConfirmRouteRestore(null)}
        />
      )}

      {editing && <EditActivityDialog activity={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
